/* pb-cart-view-tracking.js — "Viewed Cart" naar Converge

   Converge mapt dit event server-side op Criteo's `viewBasket` (zie de
   Criteo-destination in workspace Planet B: `Viewed Cart` → `viewBasket`, al
   aanwezig maar tot nu toe nooit gevuld omdat de theme het event niet stuurde).

   De unified cart drawer is de enige plek waar de shopper zijn mandje te zien
   krijgt. De /cart-pagina telt bewust niet mee: die rendert Shopify's eigen
   Liquid-`cart`, en die staat hier altijd leeg omdat pb-cart.js elke
   `/cart/add`-submit afvangt en de regels in de Storefront-cart (`pb_cart_id`)
   zet. Een event vanaf die pagina zou een mandje melden dat de shopper daar
   helemaal niet ziet.

   `view_source` zegt of de shopper zelf klikte (`drawer`) of dat het mandje
   vanzelf opensprong na een add-to-cart (`drawer_after_add`). Zo blijft de keuze
   om die automatische opens wel of niet naar Criteo te sturen een filter in de
   Converge-UI in plaats van een codewijziging.

   De payload volgt de Converge event spec voor Viewed Cart: total_price,
   total_tax, total_shipping, currency en items zijn verplicht. Tax en shipping
   kent Shopify pas in de checkout — in het mandje bestaan ze niet — dus die
   gaan als 0 mee.
*/
(function () {
  'use strict';

  var EVENT_NAME = 'Viewed Cart';

  /* pb-cart.js dispatcht `cart:open` vóór het de verse cart in zijn cache zet,
     dus een synchrone uitlezing geeft het mandje van vóór de add. Even wachten
     levert de juiste inhoud én laat de drawer eerst renderen. */
  var READ_DELAY_MS = 400;

  /* Dezelfde mandjesinhoud op dezelfde plek binnen dit venster is één view: een
     drawer die dicht en meteen weer open gaat, mag Criteo niet twee keer
     dezelfde basket sturen. */
  var DEDUPE_MS = 30000;

  var lastSignature = '';
  var lastSentAt = 0;

  /* De pixel laadt async; theme.liquid zet er een queue voor in de plaats tot
     het zover is. Dit is diezelfde queue, voor het geval wij eerst zijn. */
  function cvg() {
    if (!window.cvg) {
      var queue = function () {
        queue.process ? queue.process.apply(queue, arguments) : queue.queue.push(arguments);
      };
      queue.queue = [];
      window.cvg = queue;
    }
    return window.cvg;
  }

  function toNumber(value) {
    var n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  async function waitForPBCart(maxMs) {
    var start = Date.now();
    while (!(window.PBCart && typeof window.PBCart.current === 'function')) {
      if (Date.now() - start > (maxMs || 6000)) return false;
      await new Promise(function (r) {
        setTimeout(r, 50);
      });
    }
    return true;
  }

  /* De Storefront API geeft ID's als `gid://shopify/Product/123`, maar de
     Converge-spec gebruikt overal kale numerieke ID's — en de ad-platformen
     matchen daarop tegen hun catalogus. Criteo krijgt het GID anders letterlijk
     doorgestuurd en vindt dan niets terug. Een waarde die al kaal is, blijft
     ongemoeid. */
  function plainId(gid) {
    var value = String(gid || '');
    var match = value.match(/\/([^/]+)$/);
    return match ? match[1] : value;
  }

  function buildItems(cart, currency) {
    var edges = (cart && cart.lines && cart.lines.edges) || [];
    var items = [];

    edges.forEach(function (edge) {
      var node = edge && edge.node;
      if (!node) return;

      var merchandise = node.merchandise || {};
      var product = merchandise.product || {};
      var quantity = Math.max(1, parseInt(node.quantity, 10) || 1);

      /* Stukprijs van de variant. Een regel die door een cart transform tot
         bundel is samengevoegd draagt zijn prijs op de regel in plaats van op
         de variant — dan delen we de regeltotaal door het aantal. */
      var unitPrice =
        merchandise.price && merchandise.price.amount != null
          ? toNumber(merchandise.price.amount)
          : toNumber(node.cost && node.cost.totalAmount && node.cost.totalAmount.amount) / quantity;

      items.push({
        product_id: plainId(product.id),
        variant_id: plainId(merchandise.id),
        sku: merchandise.sku || '',
        name: product.title || '',
        variant_name: merchandise.title || '',
        price: round2(unitPrice),
        currency: currency,
        vendor: product.vendor || '',
        quantity: quantity,
      });
    });

    return items;
  }

  function lineDiscountTotal(cart) {
    var edges = (cart && cart.lines && cart.lines.edges) || [];
    var total = 0;

    edges.forEach(function (edge) {
      var allocations = (edge && edge.node && edge.node.discountAllocations) || [];
      allocations.forEach(function (allocation) {
        total += toNumber(allocation && allocation.discountedAmount && allocation.discountedAmount.amount);
      });
    });

    return total;
  }

  function buildPayload(cart, items, currency, source) {
    var cost = (cart && cart.cost) || {};
    var totalPrice = toNumber(cost.totalAmount && cost.totalAmount.amount);
    var subtotal = toNumber(cost.subtotalAmount && cost.subtotalAmount.amount);

    /* Twee soorten korting, die elkaar niet overlappen: regelkortingen staan in
       `discountAllocations` en zitten al verwerkt in het cart-subtotaal, terwijl
       een korting op cart-niveau juist het verschil tussen subtotaal en totaal
       is. Btw zit in beide bedragen (EU-prijzen zijn inclusief), dus dat verschil
       is korting en geen belasting. */
    var discount = lineDiscountTotal(cart) + Math.max(0, subtotal - totalPrice);

    var coupons = ((cart && cart.discountCodes) || [])
      .filter(function (entry) {
        return entry && entry.applicable && entry.code;
      })
      .map(function (entry) {
        return entry.code;
      });

    var payload = {
      total_price: round2(totalPrice),
      /* Pas in de checkout bekend — zie de kop van dit bestand. */
      total_tax: 0,
      total_shipping: 0,
      total_discount: round2(discount),
      currency: currency,
      items: items,

      /* Eigen properties, buiten de spec, om in Converge op te kunnen filteren. */
      view_source: source,
      cart_quantity: toNumber(cart && cart.totalQuantity),
      cart_line_count: items.length,
      page_path: window.location.pathname,
    };

    if (coupons.length) payload.coupon = coupons;

    return payload;
  }

  function signatureOf(cart, payload, source) {
    var variants = payload.items
      .map(function (item) {
        return item.variant_id + 'x' + item.quantity;
      })
      .join(',');

    return [source, (cart && cart.id) || '', payload.total_price, variants].join('|');
  }

  async function sendView(source) {
    /* Een merchant die door de customizer klikt is geen bezoeker. */
    if (window.Shopify && window.Shopify.designMode) return;
    if (!(await waitForPBCart())) return;

    var cart;
    try {
      cart = await window.PBCart.current();
    } catch (error) {
      console.warn('[ViewedCart] cart niet leesbaar', error);
      return;
    }

    var currency =
      (cart && cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.currencyCode) ||
      (cart && cart.cost && cart.cost.subtotalAmount && cart.cost.subtotalAmount.currencyCode) ||
      'EUR';

    var items = buildItems(cart, currency);
    /* Een leeg mandje is geen basket-view: Criteo kan er niets mee en Converge
       markeert het als `items_empty`. */
    if (!items.length) return;

    var payload = buildPayload(cart, items, currency, source);
    var signature = signatureOf(cart, payload, source);
    var now = Date.now();
    if (signature === lastSignature && now - lastSentAt < DEDUPE_MS) return;

    lastSignature = signature;
    lastSentAt = now;

    window.PBDebug?.log('Viewed Cart tracked', payload);

    try {
      cvg()({ method: 'track', eventName: EVENT_NAME, properties: payload });
    } catch (_error) {
      /* Een pixel die struikelt mag de drawer niet meenemen. */
    }
  }

  function scheduleView(source) {
    setTimeout(function () {
      sendView(source);
    }, READ_DELAY_MS);
  }

  /* cart-drawer-toggle.js dispatcht dit pas als de drawer echt opengaat — een
     open op een al open drawer komt hier dus niet binnen. */
  document.addEventListener('pb:cart:drawer:opened', function (event) {
    var source = (event && event.detail && event.detail.source) || 'manual';
    scheduleView(source === 'manual' ? 'drawer' : 'drawer_' + source);
  });

  /* Voor handmatig testen in de console. */
  window.PBCartViewTracking = { send: sendView };
})();
